import React, { useState } from 'react';
import { Send, Check, XCircle, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import Pagination from '../ui/Pagination';
import VisionneuseJustificatif from '../ui/VisionneuseJustificatif';
import { DemandeDotationStatutBadge } from './CentralBadges';
import { formatMontant, formatDateTime, getDotationUsageLabel } from './nosDepensesUtils';

// Sous-section calquee sur les sous-sections de MaCaisse (border-2 + en-tete pastel + tableau).
const DemandesDotationSection = ({
  demandesDotation, pagination, setPagination, isProcessingDemandeDotation,
  onValider, onRefuser
}) => {
  // La pièce qu'on regarde. Locale à la section : personne d'autre n'en a besoin.
  const [pieceConsultee, setPieceConsultee] = useState(null);

  const nbEnAttente = demandesDotation.filter((d) => d.statut === 'en_attente').length;

  return (
    <div className="border-2 border-blue-200 rounded-lg bg-blue-50/30 mb-6">
      <div className="p-4 bg-blue-50 border-b border-blue-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <Send size={20} />
            Demandes de dotation
          </h3>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            {nbEnAttente} à traiter
          </span>
        </div>
        <p className="text-sm text-blue-700 mt-1">
          Dotations demandées depuis les portefeuilles. La validation crée le mouvement et alimente la caisse bénéficiaire.
        </p>
      </div>

      <div className="p-4">
        {demandesDotation.length === 0 ? (
          <EtatVide
            icone={Send}
            titre="Aucune demande de dotation"
            description="Les demandes envoyées par les gestionnaires apparaîtront ici."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-100 border-b border-blue-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Demandeur → Bénéficiaire</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Usage</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900 uppercase">Motif</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-blue-900 uppercase">Montant</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Justificatif</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Statut</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-blue-900 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {demandesDotation.map((demande) => (
                  <tr key={demande.id} className="hover:bg-blue-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {[demande.demandeur_prenom, demande.demandeur_nom].filter(Boolean).join(' ') || '-'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        vers {[demande.beneficiaire_prenom, demande.beneficiaire_nom].filter(Boolean).join(' ') || '-'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(demande.created_at)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {getDotationUsageLabel(demande.usage_dotation)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{demande.motif || '-'}</p>
                      {demande.commentaire_decision && (
                        <p className="text-xs text-gray-500 mt-1">Décision : {demande.commentaire_decision}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="text-sm font-bold text-blue-700">{formatMontant(demande.montant)}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {/* La pièce passe par une route authentifiée : un lien
                          nu ne porte pas le jeton, et l'URL racine-relative se
                          résolvait contre l'origine du frontend — en
                          développement elle ouvrait la coquille de l'app. */}
                      {demande.justificatif_fichier_url ? (
                        <button
                          type="button"
                          onClick={() => setPieceConsultee({
                            source: 'dotation',
                            id: demande.id,
                            titre: demande.justificatif_fichier_nom || 'Justificatif',
                            sousTitre: demande.motif || getDotationUsageLabel(demande.usage)
                          })}
                          className="inline-flex p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Ouvrir le justificatif"
                        >
                          <FileText size={18} />
                        </button>
                      ) : (
                        <span className="text-sm text-red-500">Absent</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <DemandeDotationStatutBadge statut={demande.statut} />
                    </td>
                    <td className="px-4 py-3">
                      {demande.statut === 'en_attente' ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="p-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:bg-gray-300"
                            title="Valider la dotation"
                            disabled={isProcessingDemandeDotation === demande.id}
                            onClick={() => onValider(demande)}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:text-gray-300"
                            title="Refuser la dotation"
                            disabled={isProcessingDemandeDotation === demande.id}
                            onClick={() => onRefuser(demande)}
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="text-center text-xs text-gray-500">
                          <p>{formatDateTime(demande.decision_le)}</p>
                          <p>{[demande.decision_par_prenom, demande.decision_par_nom].filter(Boolean).join(' ') || '-'}</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 border-t pt-4">
          <Pagination
            page={pagination.page}
            nombrePages={pagination.total_pages}
            total={pagination.total}
            parPage={pagination.limit}
            onChangerPage={(page) => setPagination((prev) => ({ ...prev, page }))}
            libelleElement="demande"
          />
        </div>
      </div>

      <VisionneuseJustificatif
        ouvert={Boolean(pieceConsultee)}
        onFermer={() => setPieceConsultee(null)}
        source={pieceConsultee?.source}
        id={pieceConsultee?.id}
        titre={pieceConsultee?.titre}
        sousTitre={pieceConsultee?.sousTitre}
      />
    </div>
  );
};

export default DemandesDotationSection;
